//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import Icon from '@components/atoms/icon/Icon';
import { BodyText, SubtitleText } from '@components/atoms/text/Text';
import NotAvailableImage from '@images/na_rights.svg';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import { IRightsOption } from '@legacy-components/rights-management/request-rights-model';
import Cuid from '@models/Cuid';
import { SocialNetwork } from '@models/SocialNetwork';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { RIGHT_STATUSES_READ_ONLY_FORM } from '@uia/rights/constants/uiaRightsConstants';
import { useUIARightsRequestContext } from '@uia/rights/context/UIARightsRequestContext';
import { ApprovalPhraseMessage } from '@uia/rights/forms/ApprovalPhraseMessage';
import { RequestContent } from '@uia/rights/forms/RequestContent';
import {
  RightsRequestPath,
  RightsRequestPathRadioGroup,
} from '@uia/rights/forms/RequestPathRadioGroup';
import { RequestTextSource } from '@uia/rights/forms/RequestText';
import { RequestTypeTabs } from '@uia/rights/forms/RequestTypeTabs';
import { useRightsConfigurationsQuery } from '@uia/rights/hooks/useRightsConfigurationsQuery';
import { getIsRenderedWithinAnotherWorkflow } from '@uia/rights/selectors/getUIARights';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import { parseHashtags } from '@utils/stringUtils';

import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

export interface RightsRequestPathFormParams {
  approvalPhrase?: string;
  cuid: Cuid;
  requestType: RightsRequestOptions;
  rightsRequestText?: string;
  setApprovalPhrase: (value?: string) => void;
  setRequestType: (selectedValue: RightsRequestOptions) => void;
  setRightsRequestText: (value?: string) => void;
}

export const RightsRequestPathForm = ({
  approvalPhrase,
  cuid,
  requestType,
  rightsRequestText,
  setApprovalPhrase,
  setRequestType,
  setRightsRequestText,
}: RightsRequestPathFormParams) => {
  const [rightsRequestPath, setRightsRequestPath] = useState(
    RightsRequestPath.REQUEST,
  );
  const {
    requestTextIndex,
    requestTextSource,
    selectedRightsOption: selectedOption,
    setRequestTextIndex,
    setRequestTextSource,
    setSelectedRightsOption: setSelectedOption,
  } = useUIARightsRequestContext();

  const isInWorkflow = useSelector(getIsRenderedWithinAnotherWorkflow);

  const {
    data: metadata,
    isError: isErrorMetadata,
    isLoading: isLoadingMetadata,
  } = useMediaMetadataQuery(cuid);

  const { data: rightsOptions } = useRightsConfigurationsQuery(
    Cuid.toPartial(cuid),
    requestType,
  );

  if (isLoadingMetadata || isErrorMetadata) {
    return null;
  }

  const onChangeRequestText = (requestText: string) => {
    setRightsRequestText(requestText);
    const hashtags = parseHashtags(requestText);
    if (
      hashtags.length > 0 &&
      (!isDefined(approvalPhrase) || !hashtags.includes(approvalPhrase))
    ) {
      setApprovalPhrase(hashtags[0]);
    }
  };

  const onChangeOption = (option: IRightsOption) => {
    setSelectedOption(option);
    setRequestTextSource(RequestTextSource.CONFIG);
    const data = option.rightsRequestData.data;
    if (isDefined(data[requestTextIndex])) {
      onChangeRequestText(data[requestTextIndex].message);
    }
  };

  const onChangeRequestType = (newType: RightsRequestOptions) => {
    setRequestType(newType);
    setSelectedOption(undefined);

    if (
      requestTextSource !== RequestTextSource.AI &&
      requestTextSource !== RequestTextSource.CUSTOM
    ) {
      setRequestTextSource(RequestTextSource.CONFIG);
      setRightsRequestText(requestMessage);
    }

    setRequestTextIndex(0);
  };

  const {
    originalUrl,
    rightsRequestStatus: { communicationType, requestMessage, status },
    socialNetwork,
  } = metadata;

  // Fallback to default rights request message if none sent
  if (
    isUndefined(selectedOption) &&
    isUndefined(requestMessage) &&
    isDefined(rightsOptions) &&
    requestTextSource !== RequestTextSource.AI &&
    requestTextSource !== RequestTextSource.CUSTOM
  ) {
    const defaultOption = rightsOptions.find((option) => option.isDefault);
    if (isDefined(defaultOption)) {
      onChangeOption(defaultOption);
    }
  }

  // Prefill selected option if rights request message is already sent
  if (isDefined(requestMessage) && isUndefined(selectedOption)) {
    const option = rightsOptions?.find((option) =>
      option.rightsRequestData.data.some(
        (data) => data.message === requestMessage,
      ),
    );
    if (isDefined(option)) {
      onChangeOption(option);
    }
  }

  const requestContent = (
    <RequestContent
      approvalPhrase={approvalPhrase}
      communicationType={communicationType}
      cuid={metadata.rootCuid}
      onChangeOption={onChangeOption}
      requestTextIndex={requestTextIndex}
      requestTextSource={requestTextSource}
      requestType={requestType}
      rightsRequestText={rightsRequestText}
      selectedOption={selectedOption}
      setApprovalPhrase={setApprovalPhrase}
      setRequestTextIndex={setRequestTextIndex}
      setRequestTextSource={setRequestTextSource}
      setRightsRequestText={onChangeRequestText}
      setSelectedOption={setSelectedOption}
    />
  );

  const isFormReadOnly = RIGHT_STATUSES_READ_ONLY_FORM.includes(status);

  return (
    <Wrapper>
      {isInWorkflow && status !== RIGHTS_STATUS.APPROVED && (
        <RightsRequestPathRadioGroup
          rightsRequestPath={rightsRequestPath}
          setRightsRequestPath={setRightsRequestPath}
        />
      )}
      {rightsRequestPath === RightsRequestPath.REQUEST ? (
        <>
          {socialNetwork === SocialNetwork.Instagram &&
            !isDefined(originalUrl) && (
              <>
                <SubtitleText>
                  <Image alt='Not Supported' src={NotAvailableImage} />
                  Not Supported
                </SubtitleText>
                <BodyText>
                  We can't find the link to this Instagram media. Skip rights
                  above to approve this image to your Galleries without
                  requesting rights.
                </BodyText>
              </>
            )}
          {isFormReadOnly ? (
            <RequestContentWrapper>{requestContent}</RequestContentWrapper>
          ) : (
            <RequestTypeTabs
              cuid={cuid}
              requestType={requestType}
              setRequestType={onChangeRequestType}
            >
              {requestContent}
            </RequestTypeTabs>
          )}
          <ApprovalPhraseMessage
            approvalPhrase={approvalPhrase}
            communicationType={communicationType}
            socialNetwork={socialNetwork}
            status={status}
          />
        </>
      ) : (
        <FlexContainer>
          <StyledIcon name='info' />
          This image will be used without requesting permission.
        </FlexContainer>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
  `,
);

const Image = styled.img(
  ({ theme }) => `
    height: ${theme.spacings[5]};
    width: ${theme.spacings[5]};
    margin-right: ${theme.spacings[2]};
  `,
);

const FlexContainer = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: ${theme.spacings[2]};
    padding: ${theme.spacings[3]} ${theme.spacings[0]};
  `,
);

const RequestContentWrapper = styled.div(
  ({ theme }) => `
  box-shadow: ${theme.boxShadows[1]};
`,
);

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    color: ${theme.colors.aliases.bodySecondary};
  `,
);
